import { Suspense, useContext, useEffect, useState } from "react";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { IEmployee } from "../../../types/employee.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { formatDateAndTime } from "../../../common/helpers/date.helpers";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import ModalEditEmployee from "./ModalEditEmployee";

import { getCompanyId } from "../../../common/services/company.api";
import { getEmployees } from "../../../common/services/employee.api";
import { useCompanyStore } from "../../../common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import ModalCompanyAccess from "./ModalCompanyAccess";
import RemoveAceessEmployees from "./RemoveAceessEmployees";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  name: "Nome do Colaborador",
  email: "E-mail",
  type: "Tipo",
};

function TableEmployee() {
  const { auth } = useContext(AuthContext);
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });

  useEffect(() => {
    async function loadEmployees() {
      setIsLoading(true);
      setEmployees([]);

      const result = await getEmployees({
        ...filters,
        page,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (result && !result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      setEmployees(result.data);
      setIsLoading(false);
    }

    loadEmployees();
  }, [filters, page, company, companyId, data.itemsPerPage, notifyError]);

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => setAction(EMPTY_ACTION);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  const actions = [
    {
      label: "Editar",
      permission: "edit:employee",
      visible: () => auth?.user?.permissions?.includes("edit:employee") ?? false,
      action: (data: unknown) =>
        setAction({ eventName: "edit", data: (data as IEmployee)?._id }),
    },
    {
      label: "Acesso a companias",
      permission: "edit:employee",
      visible: () => auth?.user?.permissions?.includes("edit:employee") ?? false,
      action: (data: unknown) =>
        setAction({ eventName: "access", data: data as IEmployee }),
    },
    {
      label: "Cancelar acesso",
      permission: "delete:employee",
      visible: () =>
        auth?.user?.permissions?.includes("delete:employee") ?? false,
      action: (data: unknown) =>
        setAction({ eventName: "removeAccess", data: data as IEmployee }),
    },
  ];

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Colaboradores"
          placeholder="Digite o nome, o tipo ou o e-mail."
          statusOptions={{}}
          newItemLabel="Novo Colaborador"
          onNewItemClick={() =>
            setAction({ eventName: "edit", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="employee"
          tableData={employees}
        />

        <GenericTable
          headers={headers}
          data={employees}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={{
            companyId: (companyId: unknown) => {
              return getCompanySigla(companyId as string);
            },
            name: (name: unknown) => <p>{name as string}</p>,
            updatedAt: formatDateAndTime,
            createdAt: formatDateAndTime,
          }}
          entity="employee"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("edit") && (
          <ModalEditEmployee
            employeeId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("access") && (
          <ModalCompanyAccess
            employee={action.data as IEmployee}
            onClose={cleanActionData}
          />
        )}
        {isAction("removeAccess") && !!(action.data as IEmployee)?._id && (
          <RemoveAceessEmployees
            employeeId={(action.data as IEmployee)._id as string}
            employeeName={(action.data as IEmployee).name}
            onClose={cleanActionData}
            onSuccess={() => {
              const employee = action.data as IEmployee;
              setEmployees((prev) =>
                prev.filter((item) => item._id !== employee._id)
              );
              setData((prev) => ({
                ...prev,
                totalItems: prev.totalItems > 0 ? prev.totalItems - 1 : 0,
              }));
            }}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableEmployee;
